import { Navigate, Outlet } from "react-router";
import { useAuthStore } from "../stores/authStore";

/** Platform admin only route. */
export function AdminRoute() {
  const user = useAuthStore((s) => s.user);
  if (user?.platformRole !== "platform_admin") return <Navigate to="/home" replace />;
  return <Outlet />;
}

/** Platform admin or support route. */
export function SupportRoute() {
  const user = useAuthStore((s) => s.user);
  const allowed = user?.platformRole === "platform_admin" || user?.platformRole === "support";
  if (!allowed) return <Navigate to="/home" replace />;
  return <Outlet />;
}

/** Enterprise admin route — checks admin permission or enterprise_admin role key. */
export function EnterpriseAdminRoute() {
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = user?.platformRole === "platform_admin";
  const hasAdminPerm = user?.permissions?.["admin"]?.["manage_enterprise"] === true;
  const hasAdminRole = user?.roles?.some((r) => r.roleKey === "enterprise_admin") === true;
  if (!isPlatformAdmin && !hasAdminPerm && !hasAdminRole) return <Navigate to="/home" replace />;
  return <Outlet />;
}

/**
 * ConfigIQ access route — deliberately separate from EnterpriseAdminRoute.
 * ConfigIQ has its own permission model (taskInstanceCreate/Generate,
 * taskHistoryView/Enterprise, isPlatformAdmin) independent of "is this
 * person managing the enterprise itself". Gating on EnterpriseAdminRoute
 * alone would (and previously did) block:
 *   - Individual users with a platform-level configiq_user role — the
 *     entire point of the individual-user access work is unreachable if
 *     this route only checks for literal enterprise_admin.
 *   - Platform admins, who should always have full access regardless of
 *     role assignment (they bypass every ConfigIQ permission check on
 *     the backend already).
 * hasDesignRole covers configiq_user, whichever scope it was assigned at
 * (platform-level, enterprise_id null, or a specific enterprise).
 */
export function ConfigIQRoute() {
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = user?.platformRole === "platform_admin";
  const hasAdminRole = user?.roles?.some((r) => r.roleKey === "enterprise_admin") === true;
  const hasDesignRole = user?.roles?.some((r) => r.moduleKey === "design") === true;
  if (!isPlatformAdmin && !hasAdminRole && !hasDesignRole) return <Navigate to="/home" replace />;
  return <Outlet />;
}

/**
 * CostSimulator access route — deliberately separate from ConfigIQRoute.
 * Mirrors ConfigIQRoute's structure exactly but checks cost_simulator
 * module key instead of design, so a user with only a ConfigIQ role cannot
 * access CostSim and vice versa. Both products may be enabled independently
 * per enterprise. Platform admins and enterprise admins always pass — same
 * reasoning as ConfigIQRoute's own comment.
 */
export function CostSimRoute() {
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = user?.platformRole === "platform_admin";
  const hasAdminRole = user?.roles?.some((r) => r.roleKey === "enterprise_admin") === true;
  const hasCostSimRole = user?.roles?.some((r) => r.moduleKey === "cost_simulator") === true;
  if (!isPlatformAdmin && !hasAdminRole && !hasCostSimRole) return <Navigate to="/home" replace />;
  return <Outlet />;
}
